import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GATEWAY_HTTP_URL as GATEWAY_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import TopBar from '../components/TopBar';

interface InviteDetails {
  id: string;
  email: string;
  role: 'owner' | 'member';
  expiresAt: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  invitedBy: {
    name: string | null;
    email: string;
  };
}

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, login, user, getToken } = useAuth();
  const { fetchOrganizations, selectOrganization } = useOrganization();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch invite details
  useEffect(() => {
    const fetchInviteDetails = async () => {
      if (!token) {
        setError('Invalid invite link');
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${GATEWAY_URL}/api/invites/${token}`);

        if (response.data.success && response.data.invite) {
          setInvite(response.data.invite);
        } else {
          setError('Invalid or expired invite');
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('This invite link is invalid or has expired');
        } else if (err.response?.status === 410) {
          setError('This invite has already been used');
        } else {
          setError('Failed to load invite details');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInviteDetails();
  }, [token]);

  // Handle auto-accepted invites (accepted during login)
  useEffect(() => {
    const handleAutoAccepted = async () => {
      if (invite?.status === 'accepted' && isAuthenticated && !authLoading && !success) {
        setSuccess(`You've joined ${invite.organization.name}!`);

        // Refresh organizations and select the new one
        await fetchOrganizations();
        selectOrganization(invite.organization.id);

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    };

    handleAutoAccepted();
  }, [invite, isAuthenticated, authLoading, success, fetchOrganizations, selectOrganization, navigate]);

  // Handle login redirect - pass the invite URL to return to after login
  const handleLoginAndAccept = () => {
    login(`/invite/${token}`);
  };

  // Accept the invite
  const handleAccept = async () => {
    if (!token || !isAuthenticated) return;

    setIsProcessing(true);
    setError(null);

    try {
      const accessToken = await getToken();
      const response = await axios.post(
        `${GATEWAY_URL}/api/invites/${token}/accept`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );

      if (response.data.success) {
        setSuccess(`You've joined ${invite?.organization.name}!`);

        // Refresh organizations and select the new one
        await fetchOrganizations();
        if (response.data.membership?.organizationId) {
          selectOrganization(response.data.membership.organizationId);
        }

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to accept invite. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Check if invite email matches user email
  const emailMismatch = isAuthenticated && user?.email && invite?.email &&
    user.email.toLowerCase() !== invite.email.toLowerCase();

  // Check if invite is expired
  const isExpired = invite && new Date(invite.expiresAt) < new Date();

  // Format expiration date
  const formatExpirationDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
        <TopBar />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 64px)' }}>
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 animate-spin">
                <div className="absolute inset-2 rounded-full bg-white dark:bg-gray-900"></div>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400">Loading invite...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
      <TopBar />

      <div className="flex items-center justify-center px-4" style={{ height: 'calc(100vh - 64px)' }}>
        <div className="w-full max-w-md">
          {/* Error state */}
          {error && !invite && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Invalid Invite</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {/* Success state */}
          {success && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Success!</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{success}</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Redirecting to dashboard...</p>
            </div>
          )}

          {/* Invite details */}
          {invite && !success && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {invite.organization.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  You're invited to join
                </h1>
                <p className="text-xl font-semibold text-white/90">
                  {invite.organization.name}
                </p>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Invite details */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Invited by</span>
                    <span className="text-gray-900 dark:text-white font-medium">{invite.invitedBy.name || invite.invitedBy.email}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Role</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      invite.role === 'owner'
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    }`}>
                      {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Expires</span>
                    <span className={`${isExpired ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                      {isExpired ? 'Expired' : formatExpirationDate(invite.expiresAt)}
                    </span>
                  </div>
                </div>

                {/* Warnings */}
                {emailMismatch && (
                  <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Email mismatch</p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                          This invite was sent to <strong>{invite.email}</strong>, but you're logged in as <strong>{user?.email}</strong>.
                          You can still accept, but the organization admin may want to verify your identity.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {isExpired && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-red-800 dark:text-red-300">Invite expired</p>
                        <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                          This invite expired on {formatExpirationDate(invite.expiresAt)}. Please request a new invite from the organization admin.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {error && invite && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                  </div>
                )}

                {/* Actions */}
                {!isAuthenticated ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      Sign in to accept this invitation
                    </p>
                    <button
                      onClick={handleLoginAndAccept}
                      className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg"
                    >
                      Sign In to Accept
                    </button>
                  </div>
                ) : !isExpired ? (
                  <button
                    onClick={handleAccept}
                    disabled={isProcessing}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:transform-none flex items-center justify-center"
                  >
                    {isProcessing ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      'Accept Invite'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/')}
                    className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-colors"
                  >
                    Go to Dashboard
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
